/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { zodResolver } from '@hookform/resolvers/zod';
import { SoapSchema, type SoapInput, type TrialEntryInput } from '@therapy/shared-validators';
import { ArrowLeft } from 'lucide-react';
import { type SyntheticEvent, useCallback, useEffect, useMemo, useState, type JSX } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

import { Badge } from '../../components/ui/badge.js';
import { QueryState } from '../../components/ui/query-state.js';
import { type PlanGoal } from '../../lib/api/itp.js';
import { type SessionNote } from '../../lib/api/sessions.js';
import { usePlan } from '../../lib/queries/itp.js';
import {
  useSaveNote,
  useSession,
  useSessionNote,
  useSignNote,
} from '../../lib/queries/sessions.js';

import { NoteForm } from './note-form.js';
import { type TrialCounts, type TrialField } from './trials-editor.js';

const EMPTY_SOAP: SoapInput = { subjective: '', objective: '', assessment: '', plan: '' };

function toInt(value: string): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.trunc(parsed) : 0;
}

/** Seed the trials editor from an existing note, or null when there are none. */
function trialsFromNote(note: SessionNote | null | undefined): Record<string, TrialCounts> | null {
  const existing = note?.trials ?? [];
  if (existing.length === 0) {
    return null;
  }
  return Object.fromEntries(
    existing.map((tr) => [
      tr.goalId,
      {
        correct: String(tr.correct),
        incorrect: String(tr.incorrect),
        prompted: String(tr.prompted),
      },
    ]),
  );
}

function buildTrials(
  state: Readonly<Record<string, TrialCounts>>,
  goals: readonly PlanGoal[],
): TrialEntryInput[] {
  const byGoal = new Set(goals.map((g) => g.id));
  return Object.entries(state)
    .filter(([goalId]) => byGoal.has(goalId))
    .map(([goalId, counts]) => ({
      goalId,
      correct: toInt(counts.correct),
      incorrect: toInt(counts.incorrect),
      prompted: toInt(counts.prompted),
    }));
}

/** Therapist → Session Note (Figma). SOAP + per-goal trials; signing locks it. */
export function SessionNotePage(): JSX.Element {
  const { t } = useTranslation();
  const { id = '' } = useParams();
  const session = useSession(id);
  const note = useSessionNote(id);
  const itpId = session.data?.itpId ?? '';
  const plan = usePlan(itpId, itpId !== '');
  const goals = useMemo(() => plan.data?.goals ?? [], [plan.data]);

  const save = useSaveNote(id);
  const sign = useSignNote(id);
  const locked = note.data?.status === 'signed';
  const hasNote = note.data !== null && note.data !== undefined;

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<SoapInput>({
    resolver: zodResolver(SoapSchema),
    values: note.data?.soap ?? EMPTY_SOAP,
  });

  const [trials, setTrials] = useState<Record<string, TrialCounts>>({});
  useEffect(() => {
    const seeded = trialsFromNote(note.data);
    if (seeded !== null) {
      setTrials(seeded);
    }
  }, [note.data]);

  const onTrialChange = useCallback((goalId: string, field: TrialField, value: string) => {
    setTrials((prev) => {
      const current = prev[goalId] ?? { correct: '0', incorrect: '0', prompted: '0' };
      return { ...prev, [goalId]: { ...current, [field]: value } };
    });
  }, []);

  const onSubmit = useCallback(
    (event: SyntheticEvent<HTMLFormElement>) => {
      void handleSubmit((soap: SoapInput) => {
        save.mutate({ soap, trials: buildTrials(trials, goals) });
      })(event);
    },
    [handleSubmit, save, trials, goals],
  );

  const onSign = useCallback(() => {
    sign.mutate();
  }, [sign]);

  return (
    <div className="mx-auto max-w-3xl p-8">
      <Link
        to="/therapist/sessions"
        className="focus-ring mb-4 inline-flex items-center gap-1.5 rounded-lg text-sm text-slate-500 hover:text-slate-700"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        {t('therapist.note.back')}
      </Link>

      <div className="mb-6 flex items-center justify-between gap-4">
        <h1 className="text-2xl font-bold text-slate-800">{t('therapist.note.title')}</h1>
        {locked && <Badge label={t('therapist.note.signed')} tone="green" />}
      </div>

      <QueryState
        isLoading={session.isLoading || note.isLoading}
        isError={session.isError}
        isEmpty={false}
        loadingLabel={t('common.loading')}
        errorLabel={t('common.loadError')}
        emptyLabel=""
      >
        <NoteForm
          register={register}
          errors={errors}
          goals={goals}
          trials={trials}
          onTrialChange={onTrialChange}
          locked={locked}
          saving={save.isPending}
          signing={sign.isPending}
          canSign={hasNote}
          saveError={save.isError}
          onSubmit={onSubmit}
          onSign={onSign}
        />
      </QueryState>
    </div>
  );
}
